import { motion } from "framer-motion";
import { useMemo } from "react";

/**
 * Lightweight emoji confetti burst — no extra dep.
 * Renders {count} particles that fly outward + fade.
 * Must be placed in a `relative` parent. Pointer-events disabled so it never blocks UI.
 */
export default function ConfettiBurst({
  count = 18,
  emojis = ["✨", "💕", "🔥", "💫", "😈", "🌶️"],
  duration = 1.4,
}) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
        const dist = 90 + Math.random() * 90;
        return {
          id: i,
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist - 20, // bias slightly up
          rot: (Math.random() - 0.5) * 360,
          emoji: emojis[i % emojis.length],
          delay: Math.random() * 0.08,
          scale: 0.85 + Math.random() * 0.55,
        };
      }),
    [count, emojis]
  );

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-visible flex items-center justify-center"
      aria-hidden
    >
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.4, rotate: 0 }}
          animate={{
            x: p.x,
            y: p.y,
            opacity: [0, 1, 1, 0],
            scale: [0.4, p.scale, p.scale, p.scale * 0.9],
            rotate: p.rot,
          }}
          transition={{ duration, delay: p.delay, ease: "easeOut" }}
          className="absolute text-2xl select-none"
          style={{ filter: "drop-shadow(0 0 6px rgba(255,77,141,0.55))" }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  );
}
